import SettingsService from "../../services/SettingsService"
import NativeInterface from "../../services/NativeInterface"

function addSoundButton(map: google.maps.Map, callback: () => void)
{
    const controlDiv = document.createElement('div');
    const controlStyle: React.CSSProperties = {
        padding: '0',
        margin: '10px 10px 10px 10px',
        border: '2px solid white',
        borderRadius: '50%',
        cursor: 'pointer',
        backgroundColor: 'white',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.9)',
        boxSizing: 'border-box',
        lineHeight: '0',  // Reset line-height
    };
    Object.assign(controlDiv.style, controlStyle);
  
    const controlImage = document.createElement('img');
    controlImage.id = 'sound_ctrl_image';
    controlImage.src = 'img/sound-on-2.png';
    controlImage.style.width = '48px';
    controlImage.style.height = '48px';
    controlImage.style.padding = '0px';
    controlImage.style.margin = '0px';
    controlImage.style.objectFit = 'cover';
    controlImage.style.borderRadius = '50%';
    controlDiv.appendChild(controlImage);
    controlDiv.addEventListener('click', callback);
    map.controls[google.maps.ControlPosition.RIGHT_TOP].push(controlDiv);

    return controlImage as HTMLImageElement
}

class SoundIconAnimator {
    private images: string[];
    private currentIndex: number;
    private imgElement: HTMLImageElement;
    private interval?: number;  // using number for browser setInterval
    private duration = 300; // milliseconds per frame

    constructor(imgElement: HTMLImageElement) {
        this.images = [
            'img/sound-on-0.png',
            'img/sound-on-1.png',
            'img/sound-on-2.png',
            'img/sound-on-3.png'
        ];
        this.currentIndex = 0;
        this.imgElement = imgElement;
    }

    private updateImage() {
        this.imgElement.src = this.images[this.currentIndex];
        this.currentIndex = (this.currentIndex + 1) % this.images.length;
    }

    start() {
        if (!this.interval) {
            this.updateImage();  // Update once immediately, then start the interval.
            this.interval = window.setInterval(() => {
                this.updateImage();
            }, this.duration);
        }
    }

    stop() {
        if (this.interval) {
            window.clearInterval(this.interval);
            this.interval = undefined;
        }
    }
}

class AudioPlayback {
    audio?: HTMLAudioElement
    currentIndex = 0
    playList: string[] = []
    loop = false
    stopping = false
    parentController?: AudioController

    constructor(config: any, controller: AudioController) {
        if (!config) {
            return
        }

        if (!config.files || config.files.length == 0) {
            return
        }

        this.parentController = controller

        this.playList = config.files.map((file: string) => controller.makeUrl(file))

        //console.log(`-- play: ${this.playList[0]}`)

        this.audio = new Audio(this.playList[0])
        this.audio.loop = config.loop ?? false

        this.audio.muted = this.parentController.isMuted(); // to work around browser restrictions
        //this.audio.muted = true; // to work around browser restrictions

        this.audio.play()
            .then(function (this: AudioPlayback) {
                this.parentController?.onSoundStart();
//                if (this.audio) {
//                    this.audio.muted = this.parentController.isMuted();
//                }
            }.bind(this))
            .catch(error => {
                console.error(`Playback failed: file [${this.playList[0]}], error [${error}]`);
            });
        
            this.audio.addEventListener('ended', function (this: AudioPlayback) {
                if (this.stopping) {
                    return
                }

                this.currentIndex++;
            
                // If we've reached the end of the list, wrap around to the beginning
                if (this.currentIndex >= this.playList.length) {
                    if (this.loop) {
                        this.currentIndex = 0
                    } else {
                        this.deinitialize()
                        return
                    }
                }
            
                // Load and play the next audio file
                if (this.audio) {
                    this.audio.src = this.playList[this.currentIndex];
                    this.audio.play();
                }
            }.bind(this));
    }

    stop() {
        if (!this.audio || this.stopping) {
            return
        }
        this.stopping = true

        const duration = 3 * 1000 // milliseconds
        const decreaseStep = this.audio.volume / (duration / 10);
        const fadeOutInterval = setInterval(() => {
            if (this.audio && this.audio.volume > decreaseStep) {
                this.audio.volume -= decreaseStep;
            } else {
                clearInterval(fadeOutInterval);
                this.deinitialize()
            }
        }, 10); // decrease volume every 10ms
    }

    deinitialize() {
        this.parentController?.onSoundStop()
        if(this.audio){
            this.audio.pause();
            this.audio.src = ''
            delete this.audio
        }
    }

    setMuted(muted: boolean) {
        if (this.audio) {
            this.audio.muted = muted
        }
    }
}

export class AudioController {

    audioPlaybacks: { [key: string]: AudioPlayback } = {}

    mPlaybackCounter = 0
    mSoundAnimator: SoundIconAnimator
    mSoundIcon: HTMLImageElement
    mEnv: string
    mDimension: string

    mMuted = false

    mWebViewVisible = true

    mWebViewHideCallback?: () => void
    mWebViewShowCallback?: () => void

    constructor(env: string, dimension: string, map: google.maps.Map) {
        this.mEnv = env
        this.mDimension = dimension
        this.mSoundIcon = addSoundButton(map, this.onSoundBtnClick.bind(this))
        this.mSoundAnimator = new SoundIconAnimator(this.mSoundIcon)

        if (NativeInterface.isPWA()) {
            this.mWebViewHideCallback = function (this: AudioController) {
                this.mWebViewVisible = false
                this.stopAllAudio()
            }.bind(this)
            this.mWebViewShowCallback = function (this: AudioController) {
                this.mWebViewVisible = true
            }.bind(this)

            NativeInterface.subscribeOnEvent(NativeInterface.ON_WEBVIEW_HIDE, this.mWebViewHideCallback)
            NativeInterface.subscribeOnEvent(NativeInterface.ON_WEBVIEW_SHOW, this.mWebViewShowCallback)
        }
    }

    deinitialize() {
        this.stopAllAudio()
        NativeInterface.unsubscribeOnEvent(NativeInterface.ON_WEBVIEW_HIDE, this.mWebViewHideCallback)
        NativeInterface.unsubscribeOnEvent(NativeInterface.ON_WEBVIEW_SHOW, this.mWebViewShowCallback)
    }

    stopAllAudio() {
        for (const playback of Object.values(this.audioPlaybacks)) {
            playback.deinitialize()
        }
        this.audioPlaybacks = {}
    }

    makeUrl(file: string) {
        return SettingsService.makeFileUrl(this.mDimension, this.mEnv, file)
    }

    updateIconState() {
        if (this.mMuted) {
            this.mSoundAnimator.stop()
            this.mSoundIcon.src = 'img/sound-off.png'
            return
        }
        if (this.mPlaybackCounter > 0) {
            this.mSoundAnimator.start()
            return
        }
        this.mSoundAnimator.stop()
        this.mSoundIcon.src = 'img/sound-on-2.png'
    }

    onSoundStart() {
        this.mPlaybackCounter += 1
        console.log(`Sound start: ${this.mPlaybackCounter}`)
        this.updateIconState()
    }

    onSoundStop() {
        this.mPlaybackCounter -= 1
        console.log(`Sound stop: ${this.mPlaybackCounter}`)
        if (this.mPlaybackCounter <= 0) {
            this.mPlaybackCounter = 0
        }
        this.updateIconState()
    }

    updatePlaylist(playlist: { [key: string]: any }) {
        if (!this.mWebViewVisible) {
            return
        }

        for (let key in this.audioPlaybacks) {
            if (!(key in playlist)) {
                //this.audioPlaybacks[key].stop() // fading out doesn't always work on iOS. My feeling is it's timer on a deleted object issue
                this.audioPlaybacks[key].deinitialize()
                delete this.audioPlaybacks[key];
            }
        }

        for (const key in playlist) {
            if (!(key in this.audioPlaybacks)) {
                this.audioPlaybacks[key] = new AudioPlayback(playlist[key], this)
            }
        }
    }

    checkKey(key: string) {
        return key in this.audioPlaybacks
    }

    onSoundBtnClick() {
        this.mMuted = !this.mMuted

        for (const playback of Object.values(this.audioPlaybacks)) {
            playback.setMuted(this.mMuted)
        }

        this.updateIconState()
    }

    isMuted() {
        return this.mMuted
    }
}